import { useState } from "react";

export type Series = { key: string; label: string; color: string; data: number[]; fill?: boolean };

const nf = (x: number) => (Math.abs(x) >= 1000
  ? Intl.NumberFormat("id-ID", { notation: "compact", maximumFractionDigits: 1 }).format(x)
  : Intl.NumberFormat("id-ID").format(x));

/** Area + garis untuk deret waktu di permukaan terang.
 *  Aturan mark: garis 2px, grid recessive, sumbu Y hanya 3 label, crosshair +
 *  tooltip saat hover, legenda wajib untuk >= 2 deret. */
export function AreaChart({ series, labels, height = 260 }: { series: Series[]; labels: string[]; height?: number }) {
  const [hi, setHi] = useState<number | null>(null);
  const W = 900, H = height, PL = 46, PR = 12, PT = 14, PB = 26;
  const n = labels.length;
  if (!n || !series.length) {
    return <div className="avio-empty"><i className="fa-regular fa-chart-bar avio-empty__icon" /><div className="avio-empty__title">Belum ada data pada rentang ini.</div></div>;
  }
  const max = Math.max(1, ...series.flatMap((s) => s.data));
  const step = Math.pow(10, Math.floor(Math.log10(max)));
  const top = Math.ceil(max / step) * step;
  const x = (i: number) => PL + (n === 1 ? 0 : (i / (n - 1)) * (W - PL - PR));
  const y = (v: number) => PT + (1 - v / top) * (H - PT - PB);
  const path = (d: number[]) => d.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const areaPath = (d: number[]) => `${path(d)} L${x(n - 1).toFixed(1)},${y(0)} L${x(0).toFixed(1)},${y(0)} Z`;
  const ticks = [0, top / 2, top];
  const every = Math.max(1, Math.ceil(n / 8));

  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ display: "block", overflow: "visible" }}
        onPointerLeave={() => setHi(null)}
        onPointerMove={(e) => {
          const svg = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const rel = ((e.clientX - svg.left) / svg.width) * W;
          const i = Math.round(((rel - PL) / (W - PL - PR)) * (n - 1));
          setHi(Math.max(0, Math.min(n - 1, i)));
        }}>
        <defs>
          {series.map((s) => (
            <linearGradient key={s.key} id={`g-${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity="0.28" />
              <stop offset="100%" stopColor={s.color} stopOpacity="0.02" />
            </linearGradient>
          ))}
        </defs>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PL} y1={y(t)} x2={W - PR} y2={y(t)} stroke="var(--mark-grid)" strokeWidth={1} />
            <text x={PL - 8} y={y(t) + 4} textAnchor="end" fontSize={11} fill="var(--color-text-muted)">{nf(t)}</text>
          </g>
        ))}
        {series.map((s) => s.fill !== false && <path key={s.key} d={areaPath(s.data)} fill={`url(#g-${s.key})`} />)}
        {series.map((s) => (
          <path key={s.key} d={path(s.data)} fill="none" stroke={s.color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        ))}
        {hi != null && (
          <>
            <line x1={x(hi)} y1={PT} x2={x(hi)} y2={H - PB} stroke="var(--color-border-strong)" strokeWidth={1} strokeDasharray="3 3" />
            {series.map((s) => (
              <circle key={s.key} cx={x(hi)} cy={y(s.data[hi])} r={4.5} fill="var(--color-surface)" stroke={s.color} strokeWidth={2.5} />
            ))}
          </>
        )}
        {labels.map((l, i) => (i % every === 0 || i === n - 1) && (
          <text key={l} x={x(i)} y={H - 6} textAnchor="middle" fontSize={11} fill="var(--color-text-muted)">{l}</text>
        ))}
      </svg>
      {hi != null && (
        <div role="tooltip" style={{
          position: "absolute", top: 6, left: `calc(${(x(hi) / W) * 100}% )`, transform: "translateX(-50%)",
          background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)",
          boxShadow: "0 6px 18px -8px rgba(16,24,40,.45)", padding: "var(--space-4) var(--space-5)",
          fontSize: "var(--text-sm)", whiteSpace: "nowrap", pointerEvents: "none",
        }}>
          <div style={{ opacity: .68, fontSize: "var(--text-xs)", marginBottom: 3 }}>{labels[hi]}</div>
          {/* Nilai DULU, nama deret menyusul: pembaca sudah tahu deretnya, yang
              dicari angkanya. Kunci deret berupa garis pendek, bukan kotak penuh —
              pada kerapatan tooltip kotak berwarna adalah tinta sebobot data. */}
          {series.map((s) => (
            <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
              <span style={{ width: 10, height: 2, borderRadius: 1, background: s.color, flex: "none" }} />
              <b style={{ fontVariantNumeric: "tabular-nums", fontWeight: 650 }}>{Intl.NumberFormat("id-ID").format(s.data[hi])}</b>
              <span style={{ color: "var(--color-text-muted)" }}>{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
